import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, concat, map, Observable, of } from 'rxjs';
import { Chart, PostChart, PutChart, TenantAggregate, WidgetV2 } from 'src/app/interfaces/api/chart';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../core/api.service';
import { RequestService } from '../../core/request.service';
import { WebSocketService } from '../../core/web-socket.service';

type QueryParams = Record<string, string | number | boolean | Date | Array<string | number | boolean | Date> | object>;

@Injectable({
  providedIn: 'root'
})

export class ChartService {
  // Services
  private readonly request: RequestService = inject(RequestService);
  private readonly socket: WebSocketService = inject(WebSocketService)
  private readonly apiService: ApiService = inject(ApiService);
  public readonly http: HttpClient = inject(HttpClient);

  // Variables
  collection = "Chart";
  collectionPath = "charts";
  charts: Chart[] = [];
  apiUrl = environment.API_URL;

  constructor() { }

  //---GET---//

  getChartsForDashboardBatch(chartIds: string[]): Observable<WidgetV2[]> {
    if (!chartIds || chartIds.length === 0) {
      console.warn('⚠️ getChartsForDashboardBatch called with empty array');
      return of([]);
    }

    return this.apiService.postCollectionData(this.collectionPath, 'batch', {
      ids: chartIds
    }).pipe(
      map((charts: any[]) => {
        return charts as WidgetV2[];
      }),
      catchError((err) => {
        console.error(`❌ Error in POST /batch:`, err);
        return of([]);
      })
    );
  }

  getCharts(query: QueryParams = {}): Observable<any> {
    return concat(
      this.request.getCollection(this.collection, this.collectionPath, query),
      this.socket.getUpdate(this.collection)
    )
  }

  // The catalog templates (Chart.template), readable by any signed-in user; every theme without one.
  getTemplates(theme?: string): Observable<WidgetV2[]> {
    return this.apiService.getCollection(this.collectionPath, 'templates', theme ? { theme } : {});
  }

  // Template management, dev/admin only.
  addTemplate(template: Record<string, any>): Observable<WidgetV2> {
    return this.apiService.postCollectionData(this.collectionPath, 'templates', template);
  }

  updateTemplate(id: string, template: Record<string, any>): Observable<WidgetV2> {
    return this.apiService.updateCollection(this.collectionPath, `templates/${id}`, template);
  }

  deleteTemplate(id: string): Observable<any> {
    return this.apiService.deleteCollectionItem(this.collectionPath, `templates/${id}`);
  }

  getChart(id: string): Observable<any> {
    return this.request.getItem(this.collection, this.collectionPath, id)
  }

  // The content of a chart (3D model, zones, planning): left out of the dashboard batch, too heavy, and never kept
  // in the local storage.
  getChartContent(id: string): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, id).pipe(map((chart: any) => chart?.content ?? {}));
  }

  searchCharts(search: string, query: QueryParams = {}): Observable<Chart[]> {
    return this.apiService.getCollection(this.collectionPath, 'search', { q: search, ...query });
  }

  getChartsCount(query: QueryParams = {}): Observable<number> {
    return this.apiService.getCollection(this.collectionPath, 'count', query);
  }

  exportCharts(query: QueryParams = {}) {
    return this.apiService.getCollection(this.collectionPath, 'export', query);
  }

  getChartWithKey(id: string, creds: { key: string; project: string; }) {
    let options: { headers?: HttpHeaders } = {};
    if (creds.key && creds.project) {
      options["headers"] = new HttpHeaders({
        'x-access-key': creds.key,
        'x-project-id': creds.project
      })
    }

    return this.apiService.getCollectionItem(this.collectionPath, "", id, options);
  }

  getFields(query: QueryParams = {}): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'fields', query);
  }

  //---POST---//

  addChart(chart: PostChart): Observable<any> {
    return this.request.postItem(this.collection, this.collectionPath, chart);
  }

  aggregate(query: any = {}): Observable<any> {
    let options: { headers?: HttpHeaders } = {};

    if (query.key && query.project) {
      options["headers"] = new HttpHeaders({
        'x-access-key': query.key,
        'x-project-id': query.project
      })

      delete query.key;
      delete query.project;

      return this.apiService.postCollectionData(this.collectionPath, 'aggregate', query);
    } else {
      return this.apiService.postCollectionData(this.collectionPath, 'aggregate', query);
    }
  }

  // One call for every project of the tenant, merged by the API.
  aggregateTenant(tenantId: string, query: any = {}): Observable<TenantAggregate> {
    return this.apiService.postCollectionData(this.collectionPath, 'aggregate/tenant', { ...query, tenantId });
  }

  //---PUT---//

  updateChart(chart: PutChart): Observable<any> {
    return this.request.setProjectDataWithIdBody(this.collection, this.collectionPath, chart);
  }

  //---DELETE---//

  removeChart(id: string): Observable<any> {
    return this.request.deleteItem(this.collection, this.collectionPath, id)
  }

  removeCharts(query: QueryParams): Observable<any> {
    return this.request.deleteManyItems(this.collection, this.collectionPath, query)
  }
}
